import React from "react";
import { useSearchParams } from "react-router-dom";
import { useSearchParamUpdater } from "../../shared/hooks/useSearchParamUpdater";
import { AssignmentsTab } from "./AssignmentsTab";
import { CampaignsTab } from "./CampaignsTab";
import { ContentLimitsTab } from "./ContentLimitsTab";
import { CouponsTab } from "./CouponsTab";
import { CreditsTab } from "./CreditsTab";
import { OffersTab } from "./OffersTab";
import { PlansTab } from "./PlansTab";
import { useProduct } from "../../context/ProductContext";
import type { ProductLine } from "../../config/products";

const BOTH: readonly ProductLine[] = ["DRAWPOST_ADS", "POSTERMAKER"];
const DRAWPOST_ONLY: readonly ProductLine[] = ["DRAWPOST_ADS"];

const TABS = [
  { key: "plans", label: "Plans & prices", products: BOTH },
  { key: "assignments", label: "Plan assignments", products: BOTH },
  { key: "offers", label: "Offers", products: DRAWPOST_ONLY },
  { key: "coupons", label: "Coupons", products: DRAWPOST_ONLY },
  { key: "campaigns", label: "Free-days campaigns", products: BOTH },
  { key: "credits", label: "Credits", products: DRAWPOST_ONLY },
  { key: "content-limits", label: "Content limits", products: DRAWPOST_ONLY },
] as const;
type TabKey = (typeof TABS)[number]["key"];

/** What each shared tab covers, shown under the tabs. */
const SCOPE_NOTE: Partial<Record<TabKey, string>> = {
  campaigns: "Applies to new signups on every product.",
  "content-limits": "Applies to every DrawPost user, whatever their plan.",
};

const SUBTITLE: Record<ProductLine, string> = {
  DRAWPOST_ADS: "Plans, private pricing, offers, coupons, campaigns, credits and content limits.",
  POSTERMAKER: "Plans, private pricing and campaigns.",
};

/** Billing admin for the selected product: catalogue, private pricing, discounts and credits. */
const BillingScreen: React.FC = () => {
  const [searchParams] = useSearchParams();
  const updateSearchParams = useSearchParamUpdater();
  const tabParam = searchParams.get("tab");
  const { product: productLine } = useProduct();
  const tabs = TABS.filter((t) => t.products.includes(productLine));
  // A tab the selected product doesn't have falls back to Plans.
  const tab: TabKey = tabs.find((t) => t.key === tabParam)?.key ?? "plans";

  return (
    <div className="min-h-full bg-gray-50 p-4 sm:p-6">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900">Billing</h1>
        <p className="mt-1 text-sm text-gray-500">{SUBTITLE[productLine]}</p>
      </div>

      <div className="mb-4 flex gap-1 overflow-x-auto border-b border-gray-200">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => updateSearchParams({ tab: t.key })}
            className={`whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              tab === t.key ? "border-violet-600 text-violet-700" : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {SCOPE_NOTE[tab] && <p className="mb-3 text-xs text-gray-400">{SCOPE_NOTE[tab]}</p>}

      {tab === "plans" && <PlansTab key={productLine} productLine={productLine} />}
      {tab === "assignments" && <AssignmentsTab key={productLine} productLine={productLine} />}
      {tab === "offers" && <OffersTab />}
      {tab === "coupons" && <CouponsTab />}
      {tab === "campaigns" && <CampaignsTab />}
      {tab === "credits" && <CreditsTab />}
      {tab === "content-limits" && <ContentLimitsTab />}
    </div>
  );
};

export default BillingScreen;
